import { useSelector } from 'react-redux'
import { dispatch } from 'store'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import editorSlice from 'store/features/editor/slice'
import { ASPECT_RATIO_OPTIONS, TCanvasSizeKey } from 'consts'
import { MediaStatus } from 'types/media'
import { projectDataThunks } from 'store/features/projectData/thunks'
import { IMediaItem } from 'types/media'
import { ISize } from '@renderforest/webwood-engine'
import { getInitialDimensions } from 'utils'

const computeOriginalDimensions = (mediaItems: IMediaItem[]): ISize | null => {
  let maxW = 0
  let maxH = 0
  const sourceBasedItems = mediaItems.filter(item => item.type !== 'text')
  for (const item of sourceBasedItems) {
    const w =
      item.type === 'text'
        ? item.width
        : (item.meta.originalWidth ?? item.width ?? 0)
    const h =
      item.type === 'text'
        ? item.height
        : (item.meta.originalHeight ?? item.height ?? 0)
    if (w > maxW) maxW = w
    if (h > maxH) maxH = h
  }
  if (maxW <= 0 || maxH <= 0) return null
  return { width: Math.floor(maxW), height: Math.floor(maxH) }
}

const normalizeDimensions = (dimensions: ISize | null): ISize | null => {
  if (!dimensions) {
    return dimensions
  }
  const currentSmallestSize = Math.min(dimensions.width, dimensions.height)
  const initialDimensions = getInitialDimensions()
  const initialSmallestSize = Math.min(
    initialDimensions.width,
    initialDimensions.height
  )
  const normalizationMultiplier = initialSmallestSize / currentSmallestSize
  return {
    width: Math.round(dimensions.width * normalizationMultiplier),
    height: Math.round(dimensions.height * normalizationMultiplier)
  }
}

export const useAspectRatioSwitch = () => {
  const { aspectRatioString, width, height } = useSelector(
    projectDataSelectors.defaultDimensions
  )
  const mediaItems = useSelector(projectDataSelectors.mediaItems)

  const hasPendingMedia = mediaItems.some(
    item => item.meta.status === MediaStatus.PENDING
  )

  const performSwitch = (key: TCanvasSizeKey) => {
    const oldCanvas = { width, height }

    let dims: { width: number; height: number } | null = null
    if (key === 'original') {
      dims = computeOriginalDimensions(mediaItems)
      dims = normalizeDimensions(dims)
      if (!dims) {
        // No media yet — fall back to widescreen so the canvas isn't broken.
        dims = ASPECT_RATIO_OPTIONS.widescreen
      }
    } else {
      dims = ASPECT_RATIO_OPTIONS[key]
    }

    dispatch(editorSlice.actions.setSelectedAspectRatioKey(key))
    dispatch(
      projectDataThunks.setDefaultDimensions({
        width: dims.width,
        height: dims.height
      })
    )
    dispatch(projectDataThunks.refitAllMediaToCanvas(oldCanvas))
  }

  const canSwitch = !hasPendingMedia

  return {
    aspectRatioString,
    hasPendingMedia,
    performSwitch,
    canSwitch
  }
}
